import { formatLongDate } from "./patientFormatters";
import { formatTimeSlot } from "modules/appointments/helper/formatters";
import { SERVICE_TYPE } from "modules/appointments/helper/appointmentConstants";

/*
 * The header's past/upcoming appointment strip. Shared by the patient screen
 * and Create Rx — both render the same PatientHeaderCard, and a second copy of
 * this rule would be a second place for the pivot to drift.
 */

/**
 * Split a patient's appointments into past and upcoming touchpoints, dropping the
 * one currently open (it already headlines the appointment strip). Past is
 * newest-first, upcoming soonest-first; each entry is { name, date }.
 *
 * The split is relative to the OPENED APPOINTMENT's date, not to now (Nittin,
 * 2026-07-28). The strip describes that consultation's context — what came
 * before it and what follows it — so opening a visit from three months ago must
 * show the three visits before it as past and everything since as upcoming.
 * Splitting on `Date.now()` filed every one of them under "past".
 */
export const TOUCHPOINT_MAX = 3; // "max 3, not more than that" per the header design
export const toTouchpoints = (appointments, currentId, pivotDate) => {
  const parsed = new Date(pivotDate).getTime();
  // Falls back to now only when the open appointment has no usable date.
  const pivot = isNaN(parsed) ? Date.now() : parsed;
  const rows = (appointments || [])
    .filter((a) => a && a.id !== currentId && a.appointmentDate)
    /* Consultations only. The query already asks the server for them
       (PatientDetails: serviceTypeID [CONSULTATION]); this drops any row that
       still arrives with a DIFFERENT known type, so a server that ignores the
       filter cannot put a lab test or vaccine back in the strip. A row with no
       serviceTypeID is kept — absent is not evidence of another type. */
    .filter((a) => a.serviceTypeID == null || Number(a.serviceTypeID) === SERVICE_TYPE.CONSULTATION)
    .map((a) => ({ a, t: new Date(a.appointmentDate).getTime() }))
    .filter((x) => !isNaN(x.t));
  // Upcoming rows carry a start time in the dropdown (Figma 2646:3969); past
  // rows show the date alone (2646:3942). id lets a row open that appointment.
  const entry = (withTime) => ({ a }) => ({
    id: a.id,
    name: a.serviceName || "Consultation",
    date: formatLongDate(a.appointmentDate),
    time: withTime ? formatTimeSlot(a.timeSlot) : "",
  });
  return {
    past: rows
      .filter((x) => x.t < pivot)
      .sort((x, y) => y.t - x.t)
      .map(entry(false))
      .slice(0, TOUCHPOINT_MAX),
    upcoming: rows
      // `>=` so another appointment sharing this one's timestamp reads as the
      // one that follows it; the open appointment itself is already filtered out
      // by id above, so it can never appear in its own strip.
      .filter((x) => x.t >= pivot)
      .sort((x, y) => x.t - y.t)
      .map(entry(true))
      .slice(0, TOUCHPOINT_MAX),
  };
};
